import { gsap } from 'gsap';

const initializedElements = new WeakSet();

export const initCirclesLineAnimation = ({
  selector,
  root = document,
  circlesSelector = '.circle-el-wrap',
  decoratorClass = 'values-circles-decorator',
  mobileBreakpoint = 640,
  mobileMultiplier = 0.7,
  entryY = [40, 80],
  entryXGap = 50,
  rotation = [90, 180],
  peakY = [-110, -160, -210],
  observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -10% 0px',
  },
} = {}) => {
  if (!selector) {
    return;
  }

  root.querySelectorAll(selector).forEach((el) => {
    if (initializedElements.has(el)) {
      return;
    }

    initializedElements.add(el);

    const circles = el.querySelectorAll(circlesSelector);
    if (circles.length === 0) {
      return;
    }

    const decorator = document.createElement('div');
    decorator.classList.add(decoratorClass);
    el.appendChild(decorator);

    let frameId = null;
    const updateDecorator = () => {
      if (frameId) {
        return;
      }

      frameId = requestAnimationFrame(() => {
        frameId = null;

        const first = circles[0];
        const last = circles[circles.length - 1];
        const start = first.offsetLeft + first.offsetWidth / 2;
        const end = last.offsetLeft + last.offsetWidth / 2;

        decorator.style.left = `${start}px`;
        decorator.style.width = `${end - start}px`;
      });
    };

    window.addEventListener('resize', updateDecorator);
    updateDecorator();

    gsap.set(circles, {
      x: (index, target) => {
        const rect = target.getBoundingClientRect();

        return window.innerWidth - rect.left + rect.width + entryXGap;
      },
      y: () => {
        const isMobile = window.innerWidth <= mobileBreakpoint;
        const multiplier = isMobile ? mobileMultiplier : 1;

        return gsap.utils.random(entryY[0], entryY[1]) * multiplier;
      },
      rotation: () => gsap.utils.random(rotation[0], rotation[1]),
      scale: 0,
    });

    const tl = gsap.timeline({ paused: true });

    circles.forEach((circle, index) => {
      const delay = index * 0.15 + gsap.utils.random(0, 0.04);
      const duration = index === 0 ? 1.15 : (index === 1 ? 1.05 : 0.95);
      const isMobile = window.innerWidth <= mobileBreakpoint;
      const multiplier = isMobile ? mobileMultiplier : 1;
      const peak = peakY[index] ?? peakY[peakY.length - 1];
      const randomPeakY = (peak - gsap.utils.random(0, 20)) * multiplier;

      tl.to(circle, {
        scale: 1,
        opacity: 1,
        duration: duration * 0.4,
        ease: 'back.out(1.5)',
      }, delay);

      tl.to(circle, {
        x: 0,
        duration,
        ease: 'power2.out',
      }, delay);

      tl.to(circle, {
        keyframes: [
          { y: randomPeakY, duration: duration * 0.4, ease: 'power2.out' },
          { y: 0, duration: duration * 0.6, ease: 'bounce.out' },
        ],
      }, delay);

      tl.to(circle, {
        rotation: 0,
        duration,
        ease: 'power2.out',
        clearProps: 'transform',
      }, delay);
    });

    tl.to(decorator, {
      scaleX: 1,
      duration: 0.6,
      ease: 'power2.out',
    });

    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          tl.play();
          currentObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    observer.observe(el);
  });
};

initCirclesLineAnimation({
  selector: '.values-sec__circles',
});

initCirclesLineAnimation({
  selector: '.first-sec__circles',
  entryY: [56, 120],
});
